"use client"

import { css, Global } from "@emotion/react"
import styled from "@emotion/styled"
import { AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps"
import Image from "next/image"
import { Dispatch, SetStateAction, useEffect, useRef } from "react"
import { MarkerIcon, NewPhoneIcon, NewText, PinIcon, useIsDesktop, XIcon } from "ui"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { FactoryType } from "./FactoriesMap"

const StyledMarker = styled.div<{ $selected?: boolean }>`
  position: relative;
  ::after {
    display: block;
    content: "";
    position: absolute;
    top: ${(p) => (p.$selected ? "7.84px" : "3.4731px")};
    left: ${(p) => (p.$selected ? "7.99px" : "3.543px")};
    width: calc(100% - ${(p) => (p.$selected ? "15.98px" : "7.086px")});
    height: ${(p) => (p.$selected ? "56.42px" : "25px")};
    border-radius: 50%;
    background-color: white;
  }
  .markerIcon {
    position: absolute;
    left: 50%;
    top: 35%;
    transform: translate(-50%, -50%);
    z-index: 9;
  }
`

const StyledContent = styled.div`
  img {
    border-radius: 8px 8px 0px 0px;
  }
  ${(p) => p.theme.breakpoints.down("md")} {
    min-height: 104px;
    position: absolute;
    z-index: 99;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: white;
    width: calc(100% - 24px);
    border-radius: 8px;
    border: 1px solid #ece9e9;
    display: flex;
    gap: 12px;
    align-items: center;
    img {
      border-radius: 8px 0px 0px 8px;
    }
  }
`

const StyledCloseButton = styled.div`
  position: absolute;
  top: -56px;
  left: 0;
  background-color: white;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledContentTextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  ${(p) => p.theme.breakpoints.up("md")} {
    border: 1px solid #ece9e9;
    padding: 12px 12px 16px;
    border-top: none;
  }
`

const StyledLink = styled.a`
  display: flex;
  gap: 8px;
  text-decoration: none;
  height: 23px;
  align-items: center;
`

type Props = {
  branch: FactoryType
  setLocalSelect: Dispatch<SetStateAction<string>>
  localSelect: string
}

function CustomMarker({ branch, setLocalSelect, localSelect }: Props) {
  const isDesktop = useIsDesktop()
  const map = useMap()
  const eventRef = useRef<google.maps.MapsEventListener | null>(null)
  const infoWindowRef = useRef<HTMLDivElement | null>(null)

  const position = {
    lng: branch?.mapCoordinates?.address?.coordinates?.longitude,
    lat: branch?.mapCoordinates?.address?.coordinates?.latitude,
  }

  const isSelected = localSelect === branch.branchNumber
  useEffect(() => {
    if (!map || !isSelected) return

    eventRef.current = map.addListener("click", () => {
      setLocalSelect("0")
    })
    eventRef.current = map.addListener("mousedown", () => {
      setLocalSelect("0")
    })

    return () => {
      eventRef.current?.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isSelected])

  return (
    <>
      {isSelected && (
        <Global
          styles={css`
            .gm-style-iw button.gm-ui-hover-effect,
            .gm-style-iw-chr {
              display: none !important;
            }
            .gm-style-iw-c,
            .gm-style-iw-d {
              padding: 0 !important;
            }
            .gm-style-iw-c {
              width: 180px;
            }
            .gm-style-iw-d {
              overflow: hidden !important;
            }
            .gm-style-iw-tc::after {
              border: 1px solid #ece9e9;
            }
          `}
        />
      )}
      {isSelected && isDesktop && (
        <InfoWindow
          position={{
            ...position,
            lat: position.lat + 0.032,
          }}
        >
          <StyledContent ref={infoWindowRef}>
            {branch?.image?.[0]?.url && (
              <Image
                src={getUmbracoMediaUrl(branch?.image?.[0]?.url)}
                alt={branch?.image?.[0]?.altText || ""}
                width={180}
                height={140}
                style={{
                  objectFit: "cover",
                }}
              />
            )}
            <StyledContentTextBox>
              <NewText
                component={"h3"}
                desktopFontSize={20}
                desktopFontWeight={400}
                desktopLineHeight={"100%"}
                color="black"
              >
                {branch?.factoryName}
              </NewText>
              <StyledLink href={branch?.address?.[0]} target="_blank">
                <PinIcon />
                <NewText
                  component={"p"}
                  desktopFontSize={16}
                  desktopFontWeight={400}
                  desktopLineHeight={"100%"}
                  color="#2768A3"
                  style={{ textDecoration: "underline" }}
                >
                  {branch?.mapCoordinates?.address?.street}
                </NewText>
              </StyledLink>
              {branch?.phoneNumber?.[0]?.url && (
                <StyledLink
                  href={branch?.phoneNumber?.[0]?.url}
                  target={branch?.phoneNumber?.[0]?.target}
                >
                  <NewPhoneIcon />
                  <NewText
                    component={"p"}
                    desktopFontSize={16}
                    desktopFontWeight={400}
                    desktopLineHeight={"100%"}
                    color="black"
                  >
                    {branch?.phoneNumber?.[0]?.name}
                  </NewText>
                </StyledLink>
              )}
            </StyledContentTextBox>
          </StyledContent>
        </InfoWindow>
      )}
      {isSelected && !isDesktop && (
        <StyledContent
          ref={infoWindowRef}
          style={{ padding: branch?.image?.[0]?.url ? "0" : "6px 12px" }}
        >
          <StyledCloseButton onClick={() => setLocalSelect("0")}>
            <XIcon width={28} height={28} />
          </StyledCloseButton>
          {branch?.image?.[0]?.url && (
            <Image
              src={getUmbracoMediaUrl(branch?.image?.[0]?.url)}
              alt={branch?.image?.[0]?.altText || ""}
              width={104}
              height={104}
              style={{
                objectFit: "cover",
              }}
            />
          )}
          <StyledContentTextBox>
            <NewText
              component={"h3"}
              desktopFontSize={20}
              desktopFontWeight={400}
              desktopLineHeight={"100%"}
              color="black"
            >
              {branch?.factoryName}
            </NewText>
            <StyledLink href={branch?.address?.[0]} target="_blank">
              <PinIcon />
              <NewText
                component={"p"}
                desktopFontSize={16}
                desktopFontWeight={400}
                desktopLineHeight={"100%"}
                color="#2768A3"
                style={{ textDecoration: "underline" }}
              >
                {branch?.mapCoordinates?.address?.street}
              </NewText>
            </StyledLink>
            {branch?.phoneNumber?.[0]?.url && (
              <StyledLink
                href={branch?.phoneNumber?.[0]?.url}
                target={branch?.phoneNumber?.[0]?.target}
              >
                <NewPhoneIcon />
                <NewText
                  component={"p"}
                  desktopFontSize={16}
                  desktopFontWeight={400}
                  desktopLineHeight={"100%"}
                  color="black"
                >
                  {branch?.phoneNumber?.[0]?.name}
                </NewText>
              </StyledLink>
            )}
          </StyledContentTextBox>
        </StyledContent>
      )}
      <AdvancedMarker
        zIndex={isSelected ? 9 : 1}
        onClick={() => {
          setLocalSelect(branch.branchNumber)
          map?.setZoom(12)
          map?.setCenter(position)
        }}
        position={position}
      >
        <StyledMarker $selected={isSelected}>
          <MarkerIcon
            width={isSelected ? "74px" : "32.596px"}
            height={isSelected ? "96px" : "42.376px"}
            fill={`#${branch?.mapPinBackground}`}
          />
          <Image
            src={getUmbracoMediaUrl(branch?.icon?.[0]?.url)}
            alt={branch?.icon?.[0]?.altText || ""}
            width={isSelected ? 35.1707 : 15.6}
            height={isSelected ? 38 : 19}
            objectFit="contain"
            className="markerIcon"
          />
        </StyledMarker>
      </AdvancedMarker>
    </>
  )
}

export default CustomMarker
